import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { formatImage } from '../../utils/index';
import { removeTreeFromScene } from '../../utils/index'
const { CONTAINER_LIGHT_LAYER, CONTAINER_LIGHT_PRESET, getContainerLightMaterialValues } = require('../../utils/containerLight');
class ContainerObject {
    constructor(initData) {
        let { resource, env, id, params, position, rotation } = initData;
        //id
        this.id = id || 'container_' + new Date().getTime();
        //资源
        this.resource = resource;
        //环境
        this.env = env;
        //物体实例
        this.mesh = null;
        const initialParams = params || {};
        const studioShadow = env && env.isStudioMode && env.isStudioMode()
            ? { intensity: 0.78, direction: 92 }
            : { intensity: 0, direction: 92 };
        //配置项
        this.params = {
            //大小
            size: 1,
            containerLight: false,
            groupId: this.id,
            groupRotationY: 0,
            bindingOffset: null,
            ...initialParams
        };
        this.params.groupId = this.params.groupId || this.id;
        this.params.groupRotationY = this.normalizeRotationDegrees(this.params.groupRotationY);
        this.params.shadow = this.normalizeShadow(initialParams.shadow || studioShadow);
        if (this.params.visible === undefined) {
            this.params.visible = true;
        }
        this.visible = this.params.visible !== false;
        this.position = this.normalizeVector(position || this.params.position);
        this.rotation = this.normalizeVector(rotation || this.params.rotation);
        this.params.position = this.cloneVector(this.position);
        this.params.rotation = this.cloneVector(this.rotation);
        this.defaultGlbScaleBoost = 0.85;
        this.baseScale = 1;
        this.radius = 0;
        this.height = 0;
        this.lightEnhanced = false;
        this.lightSnapshots = new Map();
        this.shadowMesh = null;
        this.loader = new GLTFLoader();
        this.loader.setMeshoptDecoder(MeshoptDecoder);
        //初始化
        this.initPromise = this.init()
    }

    normalizeVector (value) {
        return {
            x: value && !Number.isNaN(parseFloat(value.x)) ? parseFloat(value.x) : 0,
            y: value && !Number.isNaN(parseFloat(value.y)) ? parseFloat(value.y) : 0,
            z: value && !Number.isNaN(parseFloat(value.z)) ? parseFloat(value.z) : 0
        };
    }

    cloneVector (value) {
        return {
            x: value.x,
            y: value.y,
            z: value.z
        };
    }

    normalizeRotationDegrees (value) {
        let numberValue = parseFloat(value);
        if (Number.isNaN(numberValue)) {
            numberValue = 0;
        }
        numberValue = numberValue % 360;
        return numberValue < 0 ? numberValue + 360 : numberValue;
    }

    normalizeShadow (value = {}) {
        const rawIntensity = value.intensity !== undefined ? value.intensity : value.opacity;
        let intensity = parseFloat(rawIntensity);
        let direction = parseFloat(value.direction);
        if (Number.isNaN(intensity)) {
            intensity = 0;
        }
        if (Number.isNaN(direction)) {
            direction = 92;
        }
        intensity = Math.max(0, Math.min(1.25, intensity));
        direction = this.normalizeRotationDegrees(direction);
        return {
            intensity,
            opacity: intensity,
            direction
        };
    }

    getScaledSize () {
        return (parseFloat(this.params.size) || 1) * this.baseScale;
    }

    getBindingOffset () {
        const customOffset = this.params.bindingOffset || this.params.insertOffset || this.params.anchorOffset;
        if (customOffset && typeof customOffset === 'object') {
            return this.normalizeVector(customOffset);
        }
        const height = Math.max(this.height || 0, 0);
        return {
            x: 0,
            y: height * 0.86 * (parseFloat(this.params.size) || 1),
            z: 0
        };
    }

    createShadowTexture () {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createRadialGradient(128, 128, 8, 128, 128, 120);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.58)');
        gradient.addColorStop(0.48, 'rgba(0, 0, 0, 0.22)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        return texture;
    }

    ensureShadowMesh () {
        if (this.shadowMesh) {
            return this.shadowMesh;
        }
        const texture = this.createShadowTexture();
        const mesh = new THREE.Mesh(
            new THREE.CircleGeometry(1, 48),
            new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                depthWrite: false,
                depthTest: true,
                side: THREE.DoubleSide
            })
        );
        mesh.name = `${this.id}_studio_shadow`;
        mesh.renderOrder = -5;
        this.shadowMesh = mesh;
        this.env.three.scene.add(mesh);
        return mesh;
    }

    updateShadow () {
        const shadow = this.normalizeShadow(this.params.shadow);
        this.params.shadow = shadow;
        const visible = !!(this.env && this.env.isStudioMode && this.env.isStudioMode() && shadow.intensity > 0.01);
        if (!visible) {
            if (this.shadowMesh) {
                this.shadowMesh.visible = false;
            }
            return;
        }
        const mesh = this.ensureShadowMesh();
        const angle = THREE.MathUtils.degToRad(shadow.direction);
        const radius = Math.max(this.radius || 0, 28);
        const size = parseFloat(this.params.size) || 1;
        const offset = Math.max(8, radius * 0.12) * size;
        const scale = Math.max(64, radius * 2.08) * size;
        mesh.visible = this.visible;
        mesh.position.set(
            this.position.x + Math.cos(angle) * offset,
            this.position.y - 2,
            this.position.z + Math.sin(angle) * offset
        );
        mesh.rotation.set(-Math.PI / 2, 0, -angle);
        mesh.scale.set(scale * 1.28, scale * 0.58, 1);
        mesh.material.opacity = Math.min(0.46, shadow.intensity * 0.42);
        mesh.material.needsUpdate = true;
    }

    markMeshOwnership () {
        if (!this.mesh) {
            return;
        }
        this.mesh.parentObject = this;
        if (this.mesh.traverse) {
            this.mesh.traverse(node => {
                node.parentObject = this;
            });
        }
    }

    isGlbContainer () {
        return !!this.resource.modelFilePath;
    }

    //初始化
    async init () {
        if (this.isGlbContainer()) {
            await this.initGlbContainer();
            return;
        }
        this.initLatheContainer();
    }

    initLatheContainer () {
        for (let data of JSON.parse(JSON.stringify(this.resource.detail))) {
            let points = data.points,
                line = [];
            points.reverse();
            for (var j = 0; j < points.length; j += 20) {
                line.push(
                    new THREE.Vector2(points[j] / 16, (j - 0) / 16)
                );
            }
            for (let vec2 of line) {
                this.radius = vec2.x > this.radius ? vec2.x : this.radius;
                this.height = vec2.y > this.height ? vec2.y : this.height;
            }
            var mesh = new THREE.Mesh(new THREE.LatheGeometry(line, 60), new THREE.MeshPhysicalMaterial({
                side: 2,
                map: new THREE.TextureLoader().load(formatImage(data.map)),
            }));
            mesh.userData.materialRole = 'container';
            this.mesh = mesh;
            this.markMeshOwnership();
            this.env.three.scene.add(mesh);
        }
        this.updateModel();
    }

    async initGlbContainer () {
        const url = formatImage(this.resource.modelFilePath);
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`模型文件请求失败(${response.status})`);
        }
        const buffer = await response.arrayBuffer();
        const gltf = await new Promise((resolve, reject) => {
            this.loader.parse(
                buffer,
                '',
                resolve,
                reject
            );
        });
        const root = gltf.scene || gltf.scenes?.[0];
        if (!root) {
            throw new Error('GLB container scene is empty');
        }
        root.traverse(child => {
            if (child.isMesh) {
                child.userData.materialRole = 'container';
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxHorizontalSize = Math.max(size.x, size.z);

        // GLB 容器的建模单位和编辑器现有器皿差异很大，先归一到编辑器可见尺度。
        const targetHeight = 100;
        const targetRadius = 145.125;
        const scaleCandidates = [];
        if (size.y > 0) {
            scaleCandidates.push(targetHeight / size.y);
        }
        if (maxHorizontalSize > 0) {
            scaleCandidates.push((targetRadius * 2) / maxHorizontalSize);
        }
        this.baseScale = scaleCandidates.length ? Math.min(...scaleCandidates) : 1;
        this.baseScale *= this.defaultGlbScaleBoost;

        root.position.x -= center.x;
        root.position.z -= center.z;
        root.position.y -= box.min.y;

        this.radius = (maxHorizontalSize / 2) * this.baseScale;
        this.height = size.y * this.baseScale;
        this.mesh = root;
        this.markMeshOwnership();
        this.env.three.scene.add(root);
        this.updateModel();
    }
    //更新模型
    updateModel () {
        if (!this.mesh) {
            return;
        }
        const scale = this.getScaledSize();
        this.mesh.scale.x = scale;
        this.mesh.scale.y = scale;
        this.mesh.scale.z = scale;
        this.mesh.visible = this.visible;
        this.mesh.position.set(this.position.x, this.position.y, this.position.z);
        this.mesh.rotation.set(
            this.rotation.x,
            this.rotation.y + THREE.MathUtils.degToRad(this.params.groupRotationY || 0),
            this.rotation.z
        );
        this.updateShadow();
    }
    setPosition (position) {
        this.position = this.normalizeVector(position);
        this.params.position = this.cloneVector(this.position);
        this.updateModel();
    }
    setRotation (rotation) {
        this.rotation = this.normalizeVector(rotation);
        this.params.rotation = this.cloneVector(this.rotation);
        this.updateModel();
    }
    setGroupRotationY (rotationY) {
        this.params.groupRotationY = this.normalizeRotationDegrees(rotationY);
        this.updateModel();
    }
    setShadow (shadow) {
        this.params.shadow = this.normalizeShadow({
            ...this.params.shadow,
            ...(shadow || {})
        });
        this.updateShadow();
    }
    setVisible (visible) {
        this.visible = visible !== false;
        this.params.visible = this.visible;
        if (this.mesh) {
            this.mesh.visible = this.visible;
        }
        this.updateShadow();
    }
    getBindingPosition () {
        const offset = this.getBindingOffset();
        return new THREE.Vector3(
            this.position.x + offset.x,
            this.position.y + offset.y,
            this.position.z + offset.z
        );
    }
    forEachMaterial (callback) {
        if (!this.mesh) {
            return;
        }
        this.mesh.traverse ? this.mesh.traverse(child => {
            if (!child.isMesh || !child.material) {
                return;
            }
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach(callback);
        }) : callback(this.mesh.material);
    }
    forEachMesh (callback) {
        if (!this.mesh) {
            return;
        }
        if (this.mesh.traverse) {
            this.mesh.traverse(child => {
                if (child.isMesh) {
                    callback(child);
                }
            });
            return;
        }
        if (this.mesh.isMesh) {
            callback(this.mesh);
        }
    }
    setContainerLightLayer (enabled = false) {
        this.forEachMesh(mesh => {
            if (!mesh.layers) {
                return;
            }
            if (enabled) {
                mesh.layers.enable(CONTAINER_LIGHT_LAYER);
            } else {
                mesh.layers.disable(CONTAINER_LIGHT_LAYER);
            }
        });
    }
    createMaterialSnapshot (material) {
        return {
            color: material.color ? material.color.clone() : null,
            emissive: material.emissive ? material.emissive.clone() : null,
            emissiveIntensity: material.emissiveIntensity,
            roughness: material.roughness,
            metalness: material.metalness,
            envMapIntensity: material.envMapIntensity,
        };
    }
    restoreMaterial (material, snapshot) {
        if (!snapshot) {
            return;
        }
        if (snapshot.color && material.color) {
            material.color.copy(snapshot.color);
        }
        if (snapshot.emissive && material.emissive) {
            material.emissive.copy(snapshot.emissive);
        }
        ['emissiveIntensity', 'roughness', 'metalness', 'envMapIntensity'].forEach(key => {
            if (typeof snapshot[key] !== 'undefined' && typeof material[key] !== 'undefined') {
                material[key] = snapshot[key];
            }
        });
        material.needsUpdate = true;
    }
    setLightEnhance (enabled = false) {
        this.lightEnhanced = enabled;
        this.params.containerLight = enabled;
        this.setContainerLightLayer(enabled);
        this.forEachMaterial(material => {
            if (!material) {
                return;
            }
            if (!enabled) {
                this.restoreMaterial(material, this.lightSnapshots.get(material));
                return;
            }
            if (!this.lightSnapshots.has(material)) {
                this.lightSnapshots.set(material, this.createMaterialSnapshot(material));
            }
            const snapshot = this.lightSnapshots.get(material);
            const values = getContainerLightMaterialValues(snapshot);
            Object.keys(values).forEach(key => {
                if (typeof material[key] !== 'undefined') {
                    material[key] = values[key];
                }
            });
            if (snapshot.color && material.color) {
                material.color.copy(snapshot.color).lerp(new THREE.Color(0xffffff), CONTAINER_LIGHT_PRESET.colorLift);
            }
            if (material.emissive) {
                material.emissive.set(0xffffff);
            }
            material.needsUpdate = true;
        });
        if (!enabled) {
            this.lightSnapshots.clear();
        }
    }
    //移除
    remove () {
        this.setLightEnhance(false);
        removeTreeFromScene(this.env.three.scene, this.mesh);
        removeTreeFromScene(this.env.three.scene, this.shadowMesh);
        this.shadowMesh = null;
        if (this.env.objects.container === this) {
            this.env.objects.container = null;
        }
        if (Array.isArray(this.env.objects.containers)) {
            const index = this.env.objects.containers.indexOf(this);
            if (index > -1) {
                this.env.objects.containers.splice(index, 1);
            }
        }
        if (this.env.selectedContainerId === this.id) {
            this.env.selectedContainerId = '';
        }
    }
}

export default ContainerObject;
